import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, ExternalLink, X } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { useBodyLock } from "../../hooks/useBodyLock";
import { Button } from "../ui";
import { fmtDate } from "../../lib/format";

export type BankReceiptPreview = {
  id: string;
  fileUrl: string;
  status: string;
  reference: string;
  createdAt: string;
  rejectReason?: string | null;
};

function isPdfUrl(url: string): boolean {
  return /\.pdf(?:\?|$)/i.test(url);
}

function receiptStatusLabel(status: string): string {
  switch (status) {
    case "PENDING":
      return "Pending verification";
    case "APPROVED":
      return "Verified";
    case "REJECTED":
      return "Rejected";
    default:
      return status;
  }
}

export function BankInReceiptViewModal({
  open,
  receipts,
  initialIndex = 0,
  onClose,
}: {
  open: boolean;
  receipts: BankReceiptPreview[];
  initialIndex?: number;
  onClose: () => void;
}) {
  const [index, setIndex] = useState(initialIndex);
  useBodyLock(open);

  useEffect(() => {
    if (open) setIndex(initialIndex);
  }, [open, initialIndex]);

  const receipt = receipts[index];
  const multi = receipts.length > 1;

  return (
    <AnimatePresence>
      {open && receipt && (
        <motion.div
          key="bank-in-receipt-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[80] flex items-end justify-center bg-black/70 p-4 backdrop-blur-sm sm:items-center"
          onClick={onClose}
          role="presentation"
        >
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.98 }}
            transition={{ type: "spring", damping: 28, stiffness: 360 }}
            className="relative flex max-h-[min(90dvh,720px)] w-full max-w-lg flex-col overflow-hidden rounded-2xl border border-white/10 bg-surface shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="bank-in-receipt-title"
          >
            <button
              type="button"
              onClick={onClose}
              className="absolute right-3 top-3 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-white/5 text-white/60 transition-colors hover:bg-white/10 hover:text-white"
              aria-label="Close"
            >
              <X size={16} />
            </button>

            <div className="min-h-0 flex-1 overflow-y-auto p-5 pb-4">
              <h2 id="bank-in-receipt-title" className="pr-8 font-display text-sm font-semibold text-white">
                Payment receipt{multi ? ` ${index + 1} of ${receipts.length}` : ""}
              </h2>
              <p className="mt-1 text-[11px] text-white/50">
                Uploaded {fmtDate(receipt.createdAt)} · {receiptStatusLabel(receipt.status)}
              </p>
              {receipt.rejectReason && (
                <p className="mt-2 rounded-xl bg-red-500/10 px-3 py-2 text-[11px] text-red-300">
                  {receipt.rejectReason}
                </p>
              )}

              <div className="mt-4 overflow-hidden rounded-xl border border-white/10 bg-black/20">
                {isPdfUrl(receipt.fileUrl) ? (
                  <iframe
                    title={`Payment receipt ${index + 1}`}
                    src={receipt.fileUrl}
                    className="h-[min(50dvh,420px)] w-full bg-white"
                  />
                ) : (
                  <img
                    src={receipt.fileUrl}
                    alt={`Payment receipt ${index + 1}`}
                    className="mx-auto max-h-[min(50dvh,420px)] w-full object-contain"
                  />
                )}
              </div>

              <a
                href={receipt.fileUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-3 inline-flex items-center gap-1 text-[11px] font-semibold text-brand-pink hover:underline"
              >
                Open full size
                <ExternalLink size={12} />
              </a>
            </div>

            <div className="flex shrink-0 gap-2 border-t border-white/5 p-4 pt-3">
              {multi && (
                <>
                  <Button
                    variant="outline"
                    className="px-3"
                    disabled={index <= 0}
                    onClick={() => setIndex((i) => Math.max(0, i - 1))}
                    aria-label="Previous receipt"
                  >
                    <ChevronLeft size={16} />
                  </Button>
                  <Button
                    variant="outline"
                    className="px-3"
                    disabled={index >= receipts.length - 1}
                    onClick={() => setIndex((i) => Math.min(receipts.length - 1, i + 1))}
                    aria-label="Next receipt"
                  >
                    <ChevronRight size={16} />
                  </Button>
                </>
              )}
              <Button className="flex-1" onClick={onClose}>
                Close
              </Button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
